import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import { CURRENCY } from "../../lib/variables";
import AppLogo from "../../assets/images/service-provider-logo.png";

// Register fonts
Font.register({
  family: "Roboto",
  src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf",
});

Font.register({
  family: "Roboto-Bold",
  src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf",
});

// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#ffffff",
    padding: 40,
    fontFamily: "Roboto",
  },

  // Header Section
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 30,
  },

  invoiceTitle: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#000000",
    marginBottom: 10,
  },

  invoiceInfo: {
    fontSize: 12,
    color: "#666666",
    marginBottom: 5,
  },

  invoiceNumber: {
    fontSize: 12,
    color: "#000000",
    fontWeight: "bold",
  },

  dateField: {
    fontSize: 12,
    color: "#000000",
  },

  // Icon placeholder (we'll use text as placeholder)
  iconPlaceholder: {
    width: 150,
    height: 150,
    justifyContent: "center",
    alignItems: "center",
  },

  // Billing Information Section
  billingSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 30,
  },

  billingColumn: {
    width: "45%",
  },

  billingTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#000000",
    marginBottom: 10,
  },

  billingText: {
    fontSize: 12,
    color: "#666666",
    marginBottom: 5,
  },

  // Itemized List Section
  itemsHeader: {
    backgroundColor: "#7922D8", // Light green
    flexDirection: "row",
    paddingVertical: 10,
    paddingHorizontal: 15,
    marginBottom: 10,
  },

  headerText: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#ffffff",
  },

  descriptionColumn: {
    width: "40%",
  },

  unitCostColumn: {
    width: "20%",
    textAlign: "right",
  },

  qtyColumn: {
    width: "20%",
    textAlign: "right",
  },

  amountColumn: {
    width: "20%",
    textAlign: "right",
  },

  // Item rows
  itemRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderBottom: "1px solid #e0e0e0",
  },

  itemText: {
    fontSize: 12,
    color: "#000000",
    textTransform: "capitalize",
  },

  // Summary Section
  summarySection: {
    marginTop: 20,
    alignItems: "flex-end",
  },

  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "300px",
    marginBottom: 5,
  },

  summaryLabel: {
    fontSize: 12,
    color: "#000000",
  },

  summaryValue: {
    fontSize: 12,
    color: "#000000",
    fontWeight: "bold",
  },

  // Total Section
  totalSection: {
    backgroundColor: "#666666",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 15,
    paddingHorizontal: 20,
    marginTop: 10,
    width: "100%",
  },

  totalLabel: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#ffffff",
  },

  totalAmount: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#ffffff",
  },

  // Terms Section
  termsSection: {
    marginTop: 30,
  },

  termsTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#000000",
    marginBottom: 5,
  },

  termsText: {
    fontSize: 12,
    color: "#666666",
  },

  // Footer Section
  footer: {
    position: "absolute",
    bottom: 40,
    right: 40,
    flexDirection: "row",
    alignItems: "center",
  },

  footerText: {
    fontSize: 10,
    color: "#666666",
    marginRight: 10,
  },

  footerBrand: {
    fontSize: 10,
    color: "#000000",
    fontWeight: "bold",
  },
});

const ServiceInvoicePDF = ({ invoiceData, currentLanguage }) => {
  const {
    userId,
    location,
    appointmentId,
    createdAt,
    platformFee,
    totalCost,
    serviceSubCategoryDetails,
    start,
  } = invoiceData || {};

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header Section */}
        <View style={styles.header}>
          <View>
            <Text style={styles.invoiceTitle}>INVOICE</Text>
            <Text style={styles.invoiceInfo}>
              Appointment ID:{" "}
              <Text style={styles.invoiceNumber}>{appointmentId}</Text>
            </Text>
            <Text style={styles.invoiceInfo}>
              Date of Appointment:{" "}
              <Text style={styles.dateField}>
                {new Date(start).toLocaleDateString()}
              </Text>
            </Text>
          </View>
          <View style={styles.iconPlaceholder}>
            {/* <Text style={{ fontSize: 8, color: "#000000" }}> */}
            <Image src={AppLogo} alt="logo" width={150} height={150} />
            {/* </Text> */}
          </View>
        </View>

        {/* Billing Information Section */}
        <View style={styles.billingSection}>
          <View style={styles.billingColumn}>
            <Text style={styles.billingTitle}>Billed to</Text>
            <Text
              style={styles.billingText}
            >{`${userId?.firstname} ${userId?.lastname}`}</Text>
            <Text style={styles.billingText}>{location?.address}</Text>
            <Text style={styles.billingText}>{location?.city}</Text>
            <Text style={styles.billingText}>{location?.zipCode}</Text>
          </View>
          <View style={styles.billingColumn}>
            <Text style={styles.billingTitle}>Eaziday</Text>
            <Text style={styles.billingText}>
              123 Business Street, Bucharest, Romania
            </Text>
            <Text style={styles.billingText}>+40 735 383 792</Text>
            <Text style={styles.billingText}>support@eaziday.com</Text>
            <Text style={styles.billingText}>https://eaziday.com/</Text>
          </View>
        </View>

        {/* Itemized List Section */}
        <View style={styles.itemsHeader}>
          <View style={styles.descriptionColumn}>
            <Text style={styles.headerText}>Service Name</Text>
          </View>
        </View>

        <View style={styles.itemRow}>
          <View style={styles.descriptionColumn}>
            <Text style={styles.itemText}>
              {serviceSubCategoryDetails?.name[currentLanguage] ||
                "Your Item name"}
            </Text>
          </View>
        </View>

        {/* Summary Section */}
        <View style={styles.summarySection}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Services Charges</Text>
            <Text style={styles.summaryValue}>
              {CURRENCY} {totalCost - platformFee}
            </Text>
          </View>

          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Platform Charges</Text>
            <Text style={styles.summaryValue}>
              {CURRENCY} {platformFee}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total</Text>
            <Text style={styles.summaryValue}>
              {CURRENCY} {totalCost}
            </Text>
          </View>
        </View>

        {/* Total Section */}
        <View style={styles.totalSection}>
          <Text style={styles.totalLabel}>Invoice total</Text>
          <Text style={styles.totalAmount}>
            {CURRENCY} {totalCost}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default ServiceInvoicePDF;
